import { axiosClientAdmin } from "@/lib/auth";
import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Comparator, FilterOperator, GetFetchDeviceDeviceData, GetFetchDeviceSnapshotDeviceResponse, GetGetDevicesDeviceData, GetGetDevicesDeviceResponse, GetGetSecondaryDevicesDeviceData, GetGetSecondaryDevicesDeviceResponse, IntegrationScriptLogDto, PostCreateDeviceDeviceData, PostCreateDeviceDeviceResponse, PostCreateDeviceExternalConnectionData, PostCreateDeviceExternalConnectionResponse, PostDeleteDeviceDeviceData, PostSetCameraActivationZoneMonitoringData, PostSetCameraActivationZoneMonitoringResponse, PostUpdateSecondaryDevicesDevice2Data, PostUpdateSecondaryDevicesDevice2Response, PostUpdateSecondaryDevicesDeviceData, PostUpdateSecondaryDevicesDeviceResponse, SecondaryDeviceFullDto, SecondaryDevicesFetched, SortOrder, DevicesSearched } from "../types.gen";
import { NvrFullDto } from "@/lib/integrationTypes/ihub-devices";
import { SearchInput, SearchInputBase } from "../util/types";

export type FetchDeviceInput = GetFetchDeviceDeviceData["path"]
export type FetchDeviceResponse = { device: NvrFullDto }

export const fetchDevice = async (input: FetchDeviceInput) => {
  const { data } = await axiosClientAdmin.get<FetchDeviceResponse>(`/Integration/Device/${input.deviceId}`);

  return data.device;
}

export const FetchDeviceQuery = (input: FetchDeviceInput) => {
  return {
    queryKey: ['device', input.deviceId],
    queryFn: () => fetchDevice(input),
    retry: (_count, error) => error instanceof AxiosError && error.status !== 404,
  } satisfies UseQueryOptions;
}

export type SearchDevicesInput = SearchInputBase & {
  query?: string
}

export type SearchDevicesResponse = DevicesSearched

// TODO: Fix up the return type? Not legit for some reason
export const searchDevices = async (
  input: SearchDevicesInput
) => {
  const path: GetGetDevicesDeviceData["url"] = "/Integration/Device/search"
  const { query, ...rest } = input

  const search = new SearchInput({
    sortBy: "name",
    sortDir: SortOrder.Ascending,
    ...rest,
  })

  if (query != null) {
    search.filterFilterGroups.push({
      operator: FilterOperator.Or,
      filters: [
        {
          field: "name",
          action: Comparator.ILike,
          value: query,
        },
        {
          field: "serialNumber",
          action: Comparator.ILike,
          value: query,
        },
        {
          field: "id",
          action: Comparator.ILike,
          value: query,
        },
      ]
    })
  }

  const { data } = await axiosClientAdmin.get<SearchDevicesResponse>(path, {
    params: search.toParams()
  })

  return data
}

export const SearchDevicesQuery = (input: SearchDevicesInput) => {
  return {
    queryKey: ["devices.search", input],
    queryFn: () => searchDevices(input),
  } satisfies UseQueryOptions
}

export type FetchSubDevicesInput = GetGetSecondaryDevicesDeviceData["path"]

export const fetchSubDevices = async (input: FetchSubDevicesInput) => {
  const { data } = await axiosClientAdmin.get<GetGetSecondaryDevicesDeviceResponse>(`/Integration/Device/${input.deviceId}/secondary`);

  // Until backend sorts these, we will sort by name manually here
  const sorted = data.secondaryDevices?.sort((a, b) => {
    return a.name?.localeCompare(b.name ?? '') ?? 0;
  })

  return sorted;
}

export const FetchSubDevicesQuery = (input: FetchSubDevicesInput) => {
  return {
    queryKey: ['device.secondary', input.deviceId],
    queryFn: () => fetchSubDevices(input),
  } satisfies UseQueryOptions;
}

export type FetchSubDeviceSnapshotInput = { id: string }
export type FetchSubDeviceSnapshotResponse = GetFetchDeviceSnapshotDeviceResponse

// Simple global throttle queue for snapshot fetches
const SNAPSHOT_MAX_CONCURRENT = 5;
const snapshotQueue: Array<() => void> = [];
let snapshotActive = 0;

const scheduleSnapshot = <T,>(task: () => Promise<T>): Promise<T> => {
  return new Promise((resolve, reject) => {
    const run = async () => {
      snapshotActive++;
      try {
        const res = await task();
        resolve(res);
      } catch (e) {
        reject(e);
      } finally {
        snapshotActive--;
        const next = snapshotQueue.shift();
        if (next) next();
      }
    };
    if (snapshotActive < SNAPSHOT_MAX_CONCURRENT) {
      run();
    } else {
      snapshotQueue.push(run);
    }
  });
};

export const fetchSubDeviceSnapshot = async (input: FetchSubDeviceSnapshotInput) => {
  return scheduleSnapshot(async () => {
    const { data } = await axiosClientAdmin.get<FetchSubDeviceSnapshotResponse>(
      `/Integration/Device/${input.id}/snapshot`
    );
    return data;
  });
}

export const FetchSubDeviceSnapshotQuery = (input: FetchSubDeviceSnapshotInput) => {
  return {
    queryKey: ['device.snapshot', input.id],
    queryFn: () => fetchSubDeviceSnapshot(input),
    staleTime: 5 * 1000,
    refetchOnWindowFocus: false,
  } satisfies UseQueryOptions;
}

export type UpdateCameraActivationZoneInput = PostSetCameraActivationZoneMonitoringData["body"] & PostSetCameraActivationZoneMonitoringData["path"]

export const updateCameraActivationZone = async (
  input: UpdateCameraActivationZoneInput
) => {
  const pathTpl: PostSetCameraActivationZoneMonitoringData["url"] = "/Monitoring/camera/{cameraId}/zone"

  const { data } = await axiosClientAdmin.post<PostSetCameraActivationZoneMonitoringResponse>(
    pathTpl.replace("{cameraId}", input.cameraId),
    { wkt: input.wkt }
  )

  return data
}

export const UpdateCameraActivationZoneMut = (cameraId: string) => {
  return {
    mutationKey: ["camera.activation-zone", cameraId] as const,
    mutationFn: (input: Pick<UpdateCameraActivationZoneInput, "wkt">) => updateCameraActivationZone({
      cameraId,
      ...input
    }),
  } satisfies UseMutationOptions<any, any, any>
}

export type UpdateDeviceInput = PostUpdateSecondaryDevicesDevice2Data["path"] & Required<PostUpdateSecondaryDevicesDevice2Data["body"]> & {
  deviceCreationChoices?: Record<string, string> | null;
};

export const updateDevice = async (input: UpdateDeviceInput) => {
  const { deviceId, ...body } = input
  const pathTpl: PostUpdateSecondaryDevicesDevice2Data["url"] = "/Integration/Device/{deviceId}/save"
  const path = pathTpl.replace("{deviceId}", deviceId)
  const { data } = await axiosClientAdmin.post<PostUpdateSecondaryDevicesDevice2Response>(path, body);

  return data;
}

export const UpdateDeviceMut = (deviceId: string) => {
  return {
    mutationKey: ["device.update", deviceId] as const,
    mutationFn: (input: Omit<UpdateDeviceInput, "deviceId">) => updateDevice({
      deviceId,
      ...input
    }),
  } satisfies UseMutationOptions<any, any, any>
}

export type DeleteDeviceInput = PostDeleteDeviceDeviceData["path"] & PostDeleteDeviceDeviceData["body"]

export const deleteDevice = async (
  input: DeleteDeviceInput
) => {
  const pathTpl: PostDeleteDeviceDeviceData["url"] = "/Integration/Device/{deviceId}/delete"
  const path = pathTpl.replace("{deviceId}", input.deviceId)
  const { data } = await axiosClientAdmin.post<PostDeleteDeviceDeviceData>(path, input);

  return data;
}

export const DeleteDeviceMut = (deviceId: string) => {
  return {
    mutationKey: ["device.delete", deviceId] as const,
    mutationFn: (input: Omit<DeleteDeviceInput, "deviceId">) => deleteDevice({
      deviceId,
      ...input
    }),
  } satisfies UseMutationOptions<any, any, any>
}

export type UpdateSecondaryDeviceInput = PostUpdateSecondaryDevicesDeviceData["path"] & PostUpdateSecondaryDevicesDeviceData["body"]

export const updateSecondaryDevice = async (
  input: UpdateSecondaryDeviceInput
) => {
  const { deviceId, ...body } = input
  const pathTpl: PostUpdateSecondaryDevicesDeviceData["url"] = "/Integration/Device/{deviceId}/secondary"
  const path = pathTpl.replace("{deviceId}", deviceId)
  const { data } = await axiosClientAdmin.post<PostUpdateSecondaryDevicesDeviceResponse>(path, body);

  return data;
}

export const UpdateSecondaryDeviceMut = (
  deviceId: string
) => {
  return {
    mutationKey: ["device.secondary.update", deviceId] as const,
    mutationFn: (input: Omit<UpdateSecondaryDeviceInput, "deviceId">) => updateSecondaryDevice({
      deviceId,
      ...input
    }),
  } satisfies UseMutationOptions<any, any, any>
}

export type CreateDeviceInput = PostCreateDeviceDeviceData["body"]

export const createDevice = async (
  input: CreateDeviceInput
) => {
  const { data } = await axiosClientAdmin.post<PostCreateDeviceDeviceResponse>(
    '/Integration/Device/connect',
    input
  )

  return data
}

export const CreateDeviceMut = () => {
  return {
    mutationKey: ["device.create"],
    mutationFn: createDevice,
  } satisfies UseMutationOptions<any, any, any>
}

export type CreateExternalConnection = PostCreateDeviceExternalConnectionData["body"]

export const createExternalConnection = async (
  input: CreateExternalConnection
) => {
  const { data } = await axiosClientAdmin.post<PostCreateDeviceExternalConnectionResponse>(
    '/Integration/ExternalConnection/create-device',
    input
  )

  return data
}

export const CreateExternalConnectionMut = () => {
  return {
    mutationKey: ["external-connection.create"],
    mutationFn: createExternalConnection,
  } satisfies UseMutationOptions<any, any, any>
}
export type ConfigureAlertingInput = { nvrId: string; setupType?: string };

export interface AlertingConfigAction {
  field: string;
  key: string;
  action: 'Create' | 'Update' | 'Delete';
  currentValue?: string | number | boolean | null;
  proposedValue: string | number | boolean;
  description?: string;
  warning?: string;
}

export interface AlertingConfigGroup {
  groupName: string;
  key: string;
  actions: AlertingConfigAction[];
  description?: string;
  warning?: string;
}

export interface AlertingConfigPlan {
  deviceId: string;
  groups: AlertingConfigGroup[];
  hasChanges: boolean;
}

export interface AlertingConfigResponse {
  scriptLog: Array<{ message: string; timestamp: string; level: string }>;
  plan: AlertingConfigPlan;
  success: boolean;
  message: string | null;
  code: string;
  errors: string[] | null;
  supportsAutoConfig: boolean;
  setupTypes: SetupType[];
}

export interface SetupType {
  value: string;
  label: string;
  description: string;
  recommended: boolean;
}

export const configureAlerting = async (input: ConfigureAlertingInput): Promise<AlertingConfigResponse> => {
  const path = `/Integration/Device/${input.nvrId}/alerting-configuration`;
  try {
    const { data } = await axiosClientAdmin.get<AlertingConfigResponse>(path, {
      params: input.setupType ? { setupType: input.setupType } : undefined,
    });
    return data;
  } catch (error) {
    // When the backend returns a non-2xx with a valid response body (e.g. 400 with scriptLog),
    // return it as data so the UI can show logs and errors inline instead of just a toast.
    if (error instanceof AxiosError && error.response?.data) {
      return error.response.data as AlertingConfigResponse;
    }
    throw error;
  }
};

export interface AlertingSetupTypesResponse {
  supportsAutoConfig: boolean;
  setupTypes: SetupType[];
}

export const fetchAlertingSetupTypes = async (nvrId: string): Promise<AlertingSetupTypesResponse> => {
  const path = `/Integration/Device/${nvrId}/alerting-setup-types`;
  const { data } = await axiosClientAdmin.get<AlertingSetupTypesResponse>(path);
  return data;
};

export const AlertingSetupTypesQuery = (nvrId: string) => {
  return {
    queryKey: ['device.alerting-setup-types', nvrId] as const,
    queryFn: () => fetchAlertingSetupTypes(nvrId),
  } satisfies UseQueryOptions<AlertingSetupTypesResponse>;
};

export type ValidationStatus = 'Pass' | 'Error' | 'Warning' | 'Unknown';

export interface ConfigurationValidationResult {
  group: string;
  validation: string;
  status: ValidationStatus;
  finding?: string;
  expected?: string;
}

interface ConfigurationValidationResponse {
  validationResults: ConfigurationValidationResult[];
  success: boolean;
  message?: string;
  code?: string;
}

export const configurationValidation = async (deviceId: string): Promise<ConfigurationValidationResult[]> => {
  const path = `/Integration/Device/${deviceId}/configuration-validate`;
  const { data } = await axiosClientAdmin.get<ConfigurationValidationResponse>(path);
  return data.validationResults;
};

export const ConfigureAlertingMut = (nvrId: string) => {
  return {
    mutationKey: ['device.configure-alerting', nvrId] as const,
    mutationFn: (setupType?: string) => configureAlerting({ nvrId, setupType }),
  } satisfies UseMutationOptions<any, any, any>;
};

export interface ApplyAlertingResult {
  changeId: string;
  success: boolean;
  error?: string;
}

export interface ApplyAlertingResponse {
  results: ApplyAlertingResult[];
  success: boolean;
}

export interface ApplyAlertingInput {
  nvrId: string;
  changeIds: string[];
}

export const applyAlertingConfiguration = async (input: ApplyAlertingInput): Promise<ApplyAlertingResponse> => {
  const path = `/Integration/Device/${input.nvrId}/alerting-configuration/apply`;
  const { data } = await axiosClientAdmin.post<ApplyAlertingResponse>(path, {
    changeIds: input.changeIds,
  });
  return data;
};

export const ApplyAlertingConfigMut = (nvrId: string) => {
  return {
    mutationKey: ['device.alerting-config-apply', nvrId] as const,
    mutationFn: (changeIds: string[]) => applyAlertingConfiguration({ nvrId, changeIds }),
  } satisfies UseMutationOptions<ApplyAlertingResponse, Error, string[]>;
};


export const moveDevice = async (input: MoveDeviceInput) => {
  const path = `/Integration/Device/${input.deviceId}/move`;
  const { data } = await axiosClientAdmin.post(path, {
    targetProjectId: input.targetProjectId,
  });
  return data;
};

export type MoveDeviceInput = {
  deviceId: string;
  targetProjectId: string;
};

export const MoveDeviceMut = (deviceId: string) => {
  return {
    mutationKey: ['device.move', deviceId] as const,
    mutationFn: (input: Omit<MoveDeviceInput, 'deviceId'>) =>
      moveDevice({ deviceId, ...input }),
  } satisfies UseMutationOptions<any, any, any>;
};


export interface DeviceValidation {
  check: string;
  result: boolean;
  message: string;
}

export interface ValidateDeviceInput {
  smtpUsername: string;
  smtpPassword: string;
  connectionSettings: Record<string, unknown>;
}

export interface ValidateDeviceResponse {
  success: boolean;
  message?: string;
  validations?: DeviceValidation[];
  deviceSmtpConfig?: Record<string, unknown>;
}

export const validateDeviceSettings = async (deviceId: string, input: ValidateDeviceInput) => {
  const path = `/Integration/Device/${deviceId}/validate-smtp`;
  const { data } = await axiosClientAdmin.post<ValidateDeviceResponse>(path, input);
  return data;
};

export const ValidateDeviceMut = (deviceId: string) => {
  return {
    mutationKey: ['device.validate', deviceId] as const,
    mutationFn: (input: ValidateDeviceInput) => validateDeviceSettings(deviceId, input),
  } satisfies UseMutationOptions<any, any, any>;
};